import EventSequence from '../../../../@types/event-sequence';
import {TestMappingProgress, TestMappingResultMessage} from '../../../../@types/test-mapping';
import {ModelLintSnapshot, ShakedownSnapshot} from '../../../../@types/model-quality';

/**
 * Module-level cache of the most recent Model Testing analysis per event sequence.
 *
 * The Model Testing modal unmounts its panels on close, which would otherwise
 * discard finished analyses the user still wants to act on (and orphan in-flight
 * runs that continue server-side). Panels restore from here on mount: finished
 * results reappear immediately, in-flight runs resume polling by runId. Entries
 * are replaced when the user explicitly starts a new run and survive until page
 * reload.
 */

export interface LintCacheEntry {
    runId: string;
    snapshot: ModelLintSnapshot | null;
    errorMessage: string | null;
}

export interface TestMappingCacheEntry {
    runId: string;
    reportId: string;
    esd: EventSequence | null;
    log: TestMappingProgress[];
    result: TestMappingResultMessage | null;
    errorMessage: string | null;
}

export interface ShakedownCacheEntry {
    runId: string;
    snapshot: ShakedownSnapshot | null;
    errorMessage: string | null;
}

/**
 * Which report source a Component was last tested against. The run itself is restored by
 * {@code useComponentMappingRun} once a source is chosen, so remembering the choice is all that is
 * needed for a finished result to still be there after the dock is closed and reopened.
 */
export interface ComponentTestCacheEntry {
    sourceName: string;
}

export interface ModelTestingCacheEntry {
    lint?: LintCacheEntry;
    testMapping?: TestMappingCacheEntry;
    shakedown?: ShakedownCacheEntry;
    componentTest?: ComponentTestCacheEntry;
}

const cacheByScope = new Map<string, ModelTestingCacheEntry>();

/**
 * The cached analyses for one thing being tested.
 *
 * Keyed by scope rather than by bare id: a Component and a Safety View can hold the same id, and a
 * numeric key would let one show the other's findings. A number still means an event sequence, so
 * the panels that only ever test one keep passing what they always did.
 *
 * @param scope the scope key from `scopeKey`, or an event sequence id
 */
export function getModelTestingCache(scope: number | string): ModelTestingCacheEntry {
    const key = typeof scope === 'number' ? `eventSequence:${scope}` : scope;
    let entry = cacheByScope.get(key);
    if (!entry) {
        entry = {};
        cacheByScope.set(key, entry);
    }
    return entry;
}
